import { cleanupTokens } from './jwt';

/**
 * Comprehensive logout function that clears all user-specific data
 * This should be called when a user logs out or when their email changes
 */
export const performComprehensiveLogout = (): void => {
  // Clear authentication tokens
  cleanupTokens();

  // Clear help box dismiss states (user-specific preferences)
  const helpBoxKeys = [
    'propertyInfoBoxDismiss',
    'fileOwnerInfoBoxDismiss',
    'watchedCertInfoBoxDismiss',
    'dailyEmailInfoBoxDismiss',
    'contactInfoBoxDismiss',
  ];

  helpBoxKeys.forEach((key) => {
    localStorage.removeItem(key);
  });

  // Clear user-specific filters and preferences
  const userSpecificKeys = [
    'myTasksFilter',
    'teamTasksFilter',
    'myApplicantsFilter',
    'filtersExpanded', // Keep for backward compatibility (will be removed by migration)
    'notificationFilters', // New persistent notification filters
    'notificationsSettingsAlertDismissed',
    'tenantCurrentCertificationID',
    'syncedForms',
    'configure_cols_ver',
    'fromSettingsTour',
    'fromHeaderTour',
    'nextUrl',
  ];

  userSpecificKeys.forEach((key) => {
    localStorage.removeItem(key);
  });

  // Clear any configure columns data (user-specific table preferences)
  const configureColsKeys = Object.keys(localStorage).filter(
    (key) => key.startsWith('configure_cols_') && !key.includes('ver')
  );
  configureColsKeys.forEach((key) => {
    localStorage.removeItem(key);
  });

  // Clear My Work board filter state (scoped per board owner by targetUserId).
  // These keys store structural filters (property, category, status, date) that
  // may be sensitive in a compliance context and must not survive logout.
  const myWorkFilterKeys = Object.keys(localStorage).filter((key) =>
    key.startsWith('myWorkBoardFilters_')
  );
  myWorkFilterKeys.forEach((key) => {
    localStorage.removeItem(key);
  });

  // Clear pagination preferences (user-specific)
  const paginationKeys = Object.keys(localStorage).filter(
    (key) => key.includes('rowsPerPage') || key.includes('pagination')
  );
  paginationKeys.forEach((key) => {
    localStorage.removeItem(key);
  });

  // Clear refresh indicators
  const refreshKeys = Object.keys(localStorage).filter(
    (key) => key.includes('refresh') || key.includes('Refresh')
  );
  refreshKeys.forEach((key) => {
    localStorage.removeItem(key);
  });

  // Clear any other user-specific data
  const userDataKeys = Object.keys(localStorage).filter(
    (key) =>
      key.includes('user') || key.includes('User') || key.includes('current')
  );
  userDataKeys.forEach((key) => {
    localStorage.removeItem(key);
  });

  // Redirect to home page
  window.location.href = '/';
};

/**
 * Simple logout function that only clears authentication tokens
 * This preserves local storage (tours, preferences, etc.)
 * Use this for password reset scenarios where user will return
 */
export const performSimpleLogout = (): void => {
  // Clear only authentication tokens
  cleanupTokens();

  // Remove old token formats for compatibility
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');

  // Force page reload to clear session
  window.location.reload();
};

/**
 * Backend logout function that calls the Django logout endpoint
 * This cleans up server-side session and cookies
 */
export const performBackendLogout = async (): Promise<void> => {
  try {
    await fetch(
      (import.meta.env.VITE_API_ROOT || 'http://localhost:8000/api/') +
        'logout/',
      {
        method: 'POST',
        credentials: 'include', // Include cookies for proper logout
      }
    );
  } catch {
    // Continue even if backend logout fails
  }
};

/**
 * Complete logout with backend cleanup
 * This calls backend logout and then performs comprehensive frontend logout
 */
export const performCompleteLogout = async (): Promise<void> => {
  // First, clean up backend session
  await performBackendLogout();

  // Then, perform comprehensive frontend logout
  performComprehensiveLogout();
};

/**
 * Password reset logout - preserves local storage but cleans up session
 * This is specifically for password reset scenarios
 */
export const performPasswordResetLogout = async (): Promise<void> => {
  // Clean up backend session
  await performBackendLogout();

  // Perform simple frontend logout (preserves local storage)
  performSimpleLogout();
};

/**
 * Logout function specifically for email changes
 * This performs a comprehensive logout and shows appropriate messaging
 */
export const logoutForEmailChange = (): void => {
  // Perform comprehensive logout
  performComprehensiveLogout();
};
